<script lang="ts">
    import {
        VpAddIcon,
        VpCalendarIcon,
        VpEditIcon,
        VpMapPinIcon,
        VpRunIcon,
        VpUserIcon,
        VpWalkIcon,
    } from '@decathlon-experience/icons/svelte';

    import { trans } from '$assets/shop-translator.js';
    import Button from '$saga/components/Button.svelte';
    import Loader from '$saga/components/Loader.svelte';
    import alpine_skiing from '$saga/icons/sports/alpine_skiing.svg';
    import hiking from '$saga/icons/sports/hiking.svg';
    import surf from '$saga/icons/sports/surf.svg';
    import { filtersContext } from '$saga/views/Search/store';
    import { FilterType } from '$saga/views/Search/types';
    import { SEARCH_PAGE_FORM_ID } from '$saga/views/Search/utils';

    export let handleSelectedFilter;
    export let trigger;

    // TODO pass ids down to svelte from server
    const quickSports = [
        { sportValue: '5', icon: hiking, labelKey: 'search.bar.hiking' },
        { sportValue: '23', icon: surf, labelKey: 'search.bar.surf' },
        {
            sportValue: '30',
            icon: alpine_skiing,
            labelKey: 'search.bar.alpine_skiing',
        },
    ];

    $: sportCount = $filtersContext.formLabels.theme.length;

    $: pillars = [
        {
            type: FilterType.SPORT,
            icon: VpRunIcon,
            label: trans('search.bar.sport'),
            count: sportCount,
        },
        {
            type: FilterType.DESTINATION,
            icon: VpMapPinIcon,
            label: trans('search.bar.destination'),
            count: $filtersContext.formLabels.destination.length,
        },
        {
            type: FilterType.STARTDATE,
            icon: VpCalendarIcon,
            label: trans('search.bar.departure_date'),
            mobileLabel: trans('search.bar.departure_dates'),
            count:
                $filtersContext.formLabels.startDate != null ||
                $filtersContext.formLabels.months.length > 0
                    ? 1
                    : 0,
        },
        {
            type: FilterType.PAX,
            icon: VpUserIcon,
            label: trans('search.bar.travelers'),
            count:
                $filtersContext.formLabels.nbPax !== '' &&
                $filtersContext.formLabels.nbPax !== '0'
                    ? 1
                    : 0,
        },
    ];

    const selectQuickSport = (sportValue: string) => {
        handleSelectedFilter(FilterType.SPORT);
        const sport = $filtersContext?.defaultSportOptions?.find(
            (sportOption) => sportOption.value === sportValue,
        );
        if (sport) {
            $filtersContext.formValues.theme = [sport.value];
            $filtersContext.formLabels.theme = [sport.label];
        }
    };

    const withCount = (label: string, count: number) =>
        count > 0 ? `${label} (${count})` : label;
</script>

<!-- dekstop -->
<div class="hidden desktop:flex gap-s items-center justify-center w-full">
    {#each pillars as pillar (pillar.type)}
        <button
            class="flex w-full items-center justify-between bg-container-quiet p-s hover:bg-container-commercial-neutral rounded-s"
            on:click|preventDefault|stopPropagation={() => {
                handleSelectedFilter(pillar.type);
            }}
            {...$trigger}
            use:trigger
        >
            <span
                class="flex items-center gap-xs whitespace-nowrap text-sm"
                class:text-content-brand={pillar.count > 0}
            >
                <svelte:component this={pillar.icon} class="size-m" />
                {withCount(pillar.label, pillar.count)}
            </span>
            <svelte:component
                this={pillar.count > 0 ? VpEditIcon : VpAddIcon}
                size={16}
            />
        </button>
    {/each}
    <Button
        type="submit"
        form={SEARCH_PAGE_FORM_ID}
        data-turbo={false}
        class="w-full normal-case"
        shape="rounded"
    >
        {#if $filtersContext.isSubmitting}
            <Loader class="w-xl h-xl" color="white" />
        {:else}
            {trans('search.bar.search')}
        {/if}
    </Button>
</div>

<!-- mobile -->
<div
    class="desktop:hidden flex flex-col gap-m items-center justify-center w-full"
>
    <div class="flex w-full flex-col gap-s">
        <div class="w-full bg-container-quiet px-m py-xs rounded-s">
            <div class="mb-xs flex items-center gap-xs">
                <VpWalkIcon
                    size={16}
                    class={sportCount > 0 ? 'text-content-brand' : ''}
                />
                <span
                    class="font-semibold"
                    class:text-content-brand={sportCount > 0}
                >
                    {withCount(trans('search.bar.sport'), sportCount)}
                </span>
            </div>
            <div class="flex justify-evenly sm:justify-start sm:gap-2xl">
                {#each quickSports as sport (sport.sportValue)}
                    <div
                        class="flex flex-1 flex-col items-center gap-2xs sm:flex-initial"
                    >
                        <button
                            on:click|preventDefault|stopPropagation={() => {
                                selectQuickSport(sport.sportValue);
                            }}
                            {...$trigger}
                            use:trigger
                            class="flex h-fit w-fit items-center justify-center rounded-[12px] bg-white p-xs shadow-light"
                        >
                            <img
                                src={sport.icon}
                                alt={trans(sport.labelKey)}
                                class="size-3xl"
                            />
                        </button>
                        <span class="text-center text-xs"
                            >{trans(sport.labelKey)}</span
                        >
                    </div>
                {/each}
                <div
                    class="flex flex-1 flex-col items-center gap-2xs sm:flex-initial"
                >
                    <button
                        on:click|preventDefault|stopPropagation={() => {
                            handleSelectedFilter(FilterType.SPORT);
                        }}
                        {...$trigger}
                        use:trigger
                        class="flex h-fit w-fit items-center justify-center rounded-[12px] bg-white p-xs shadow-light"
                    >
                        <span class="h-3xl w-3xl text-[28px] font-semibold"
                            >...</span
                        >
                    </button>
                    <span class="text-center text-xs"
                        >{trans('search.bar.other_sport')}</span
                    >
                </div>
            </div>
        </div>
        {#each pillars.filter((pillar) => pillar.type === FilterType.DESTINATION || pillar.type === FilterType.STARTDATE) as pillar (pillar.type)}
            <button
                on:click|preventDefault|stopPropagation={() => {
                    handleSelectedFilter(pillar.type);
                }}
                {...$trigger}
                use:trigger
                class="w-full bg-container-quiet p-s rounded-s"
            >
                <span class="flex items-center gap-xs">
                    <svelte:component
                        this={pillar.icon}
                        size={16}
                        class={pillar.count > 0 ? 'text-content-brand' : ''}
                    />
                    <span
                        class="font-semibold"
                        class:text-content-brand={pillar.count > 0}
                    >
                        {withCount(
                            pillar.mobileLabel ?? pillar.label,
                            pillar.count,
                        )}
                    </span>
                    <VpAddIcon size={16} class="ml-auto" />
                </span>
            </button>
        {/each}
    </div>
    <button
        class="vp-button vp-button-small vp-button--squared w-full normal-case rounded-full"
        {...$trigger}
        use:trigger
    >
        {#if $filtersContext.isSubmitting}
            <Loader class="w-xl h-xl" color="white" />
        {:else}
            <span class="flex items-center gap-xs"
                >{trans('search.bar.search_trip')}</span
            >
        {/if}
    </button>
</div>
